import { ComponentType, useEffect, useRef } from 'react';

import { Search } from 'ui/icons/action/Search';
import { InputText } from 'ui/inputs/inputText/InputText';
import { BasicSelect } from './BasicSelect';
import { MultiSelect } from './MultiSelect';
import { MultiSelectProps, SelectProps, SelectValueType, SingleSelectProps } from './types';

type Props<TValue extends SelectValueType = string> = {
  autoFocusOnInput?: boolean;
  defaultValue?: TValue;
  /** optional text */
  inputContent?: {
    triggerPlaceholder?: string;
    filterPlaceholder: string;
  };
  isDisabled?: boolean;
};

export type FilterSelectProps<
  TValue extends SelectValueType = string,
  TProps = SingleSelectProps,
> = SelectProps<TValue> & Props<TValue> & TProps;

const baseContent = {
  triggerPlaceholder: 'Search',
  filterPlaceholder: 'Search',
};

export function createFilterSelect<TProps = SingleSelectProps>(
  BaseComponent: ComponentType<React.PropsWithChildren<SelectProps & TProps>>,
) {
  return function CustomFilterSelect<TValue extends SelectValueType = string>({
    autoFocusOnInput,
    defaultValue,
    defaultValues = [],
    inputContent = baseContent,
    isLoading = false,
    isDisabled = false,
    ...props
  }: FilterSelectProps<TValue, TProps>) {
    const inputRef = useRef<HTMLInputElement>(null);
    let initialValues: TValue[] = [];
    if (defaultValues.length > 0) {
      initialValues = defaultValues;
    } else if (defaultValue) {
      initialValues = [defaultValue];
    }

    const Component = BaseComponent as unknown as ComponentType<
      React.PropsWithChildren<SelectProps<TValue> & (MultiSelectProps | SingleSelectProps)>
    >;

    useEffect(() => {
      /**
       * Refocus the input field when `isLoading` changes to ensure
       * the search box retains focus after the dropdown updates its state.
       */
      if (autoFocusOnInput && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isLoading]);

    return (
      <Component
        placeholder={inputContent.triggerPlaceholder}
        autoFocusOnInput={autoFocusOnInput}
        {...props}
        defaultValues={initialValues}
        isLoading={isLoading}
        isDisabled={isDisabled}
      >
        <InputText
          inputRef={inputRef}
          type="search"
          autoComplete="off"
          id={`${props.id}-input-filter`}
          placeholder={inputContent.filterPlaceholder}
          Prefix={<Search />}
          value=""
          autoFocus={autoFocusOnInput}
          isLoading={isLoading}
          isDisabled={isDisabled}
          applyStickyStyles
        />
      </Component>
    );
  };
}

export const BasicFilterSelect = createFilterSelect<SingleSelectProps>(BasicSelect);
export const MultiFilterSelect = createFilterSelect<MultiSelectProps>(MultiSelect);
